// src/views/words-list/WordsListView.tsx
/* eslint-disable max-len */
/* eslint-disable no-param-reassign */
import React, {
  FC, useCallback, useEffect, useState, useRef, useMemo,
} from 'react';
import { nanoid } from 'nanoid';
import { Formik, FormikProps, useFormikContext } from 'formik';
import { Form, SubmitButton } from 'formik-antd/lib';
import { isEqual } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button, Card, Modal, Spin, Tooltip,
} from 'antd';
import {
  TbCircleCheck, TbDeviceFloppy, TbListDetails, TbPhotoPlus, TbSquareRoundedPlus,
} from 'react-icons/tb';
import { ReactComponent as Thrash } from '../../assets/icons/teacher/trash.svg';
import classes from './WordsListView.module.less';
import styles from './WordsListView.styles';
import withTagManager from '../../hooks/withTagManager';
import FormItem from '../../components/FormItem/FormItem';
import { getValidationSchema, MAX_WORD_FIELD_LENGTH } from './WordsList.schema';
import {
  getIsLoadingWordsListData, getIsSaving, getIsSavingError, getWordsListData,
} from './slice/selectors';
import { fetchWordsListData, saveWordList } from './slice/actions';
import { error, success } from '../../components/Message';
import useActionStatus from '../../hooks/useActionStatus';
import { getActiveList } from '../auth/slice/selectors';
import { setActiveList } from '../auth/slice/actions';
import { ImageSelectionDrawer, SelectedImage } from '../../components/ImageSelectionDrawer/ImageSelectionDrawer';
import { isUnsplashWithoutAuthor, withUnsplashUtm } from '../../utils/unsplashAttribution';
import { pluralForm } from '../../utils/plural';
import { getWordsToFill, useWordsCatalog } from './useWordsCatalog';
import { CatalogWordButton } from './CatalogWordButton';

// Auto-save co 120 sekund
const AUTO_SAVE_INTERVAL = 120000;

// Stała referencja, żeby nie psuć `memo` w FormItem
const WORD_FIELD_PROPS = { maxLength: MAX_WORD_FIELD_LENGTH };

const hasTerm = (word: any): boolean => Boolean(String(word?.term ?? '').trim());

// Ostatnia zapisana wersja każdej karty po id. Id powtórzone albo brakujące (stare listy) nie wskazuje
// jednej karty — wtedy `null`
const getSavedWordsById = (savedWords: any[]): Map<any, any> => {
  const savedWordsById = new Map<any, any>();

  savedWords.forEach((word) => {
    savedWordsById.set(word?.id, savedWordsById.has(word?.id) || word?.id == null ? null : word);
  });

  return savedWordsById;
};

// Karta bez zwrotu nie trafia na serwer w nowej postaci. Nowa nie idzie wcale (`undefined`), a za zapisaną
// idzie jej ostatnia zapisana wersja — pominięta zniknęłaby z serwera. Karta z niejednoznacznym id idzie
// jak jest, bo nie wiadomo, która zapisana wersja jest jej
const getWordToSave = (word: any, savedWordsById: Map<any, any>): any => {
  if (hasTerm(word)) {
    return word;
  }

  if (!savedWordsById.has(word?.id)) {
    return undefined;
  }

  return savedWordsById.get(word?.id) ?? word;
};

// Serwer odsyła dokładnie wysłane karty, w tej samej kolejności. Niewysłane nowe karty wracają na swoje
// miejsca z formularza — inaczej przeładowanie po autozapisie zabierałoby je w trakcie wypełniania.
// Zapisana karta z wyczyszczonym zwrotem wraca w wersji z serwera, czyli ze starym zwrotem
const withUnsavedWords = (savedWords: any[], submitted: { values: { words: any[] }; sent: any[] } | null): any[] => {
  if (
    !submitted
    || !submitted.sent.includes(undefined)
    || savedWords.length !== submitted.sent.filter((word) => word !== undefined).length
  ) {
    return savedWords;
  }

  const remaining = [...savedWords];

  return submitted.values.words.map((word, index) => (submitted.sent[index] === undefined ? word : remaining.shift()));
};

// Podpowiedź przy czerwonym napisie „Zwrot” — tylko w karcie, której zapis nie wyśle w obecnej postaci
const getTermRequiredKey = (word: any, savedWordsById: Map<any, any>): string | undefined => {
  if (!word || hasTerm(word) || savedWordsById.get(word.id) === null) {
    return undefined;
  }

  return savedWordsById.has(word.id) ? 'words-list:wordsListForm.info.termRequiredSaved' : 'words-list:wordsListForm.info.termRequired';
};

interface SaveButtonProps {
  savedValues: { name: string; description: string; words: any[] };
  isSubmitting: boolean;
}

// „Zapisz zmiany” (z dyskietką) albo przygaszone, nieklikalne „Zapisano” (z ptaszkiem w kółku), gdy formularz niczym się
// nie różni od listy na serwerze. Karta czekająca na zwrot to niezapisana zmiana: jest na ekranie, ale serwer jej nie ma
const SaveButton: FC<SaveButtonProps> = ({ savedValues, isSubmitting }) => {
  const { t } = useTranslation();
  const { values } = useFormikContext();
  const isSaved = isEqual(values, savedValues);

  return (
    <SubmitButton
      type="primary"
      id={classes.submitt}
      className={isSaved ? classes.saved : undefined}
      loading={isSubmitting}
      disabled={isSubmitting || isSaved}
    >
      {t(isSaved ? 'words-list:button.saved' : 'words-list:button.save')}
      {isSaved ? <TbCircleCheck className={classes.saveIcon} /> : <TbDeviceFloppy className={classes.saveIcon} />}
    </SubmitButton>
  );
};

const WordsListView: FC = () => {
  const { id } = useParams<{ id?: string | undefined }>();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [words, setWords] = useState<any[]>([]);
  const [selectedWordId, setSelectedWordId] = useState<string | null>(null);
  const [selectedWordTerm, setSelectedWordTerm] = useState<string>('');
  const activeList = useSelector(getActiveList);

  const loading = useSelector(getIsLoadingWordsListData);
  const wordsList = useSelector(getWordsListData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formikRef = useRef<FormikProps<any> | null>(null);
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  // Formularz z chwili ostatniego zapisu i to, co za każdą jego kartą wysłaliśmy
  const submittedRef = useRef<{ values: any; sent: any[] } | null>(null);
  // Autozapis woła ten sam submit co „Zapisz zmiany” — flaga mówi, że to on
  const isAutoSaveRef = useRef(false);

  // ✅ FIX 1: Flaga kontrolująca czy dane są zsynchronizowane z ID
  const [isDataSynced, setIsDataSynced] = useState(false);

  // „Zapisz zmiany” wisi nad kartami, dopóki koniec formularza jest poza ekranem — wtedy dostaje cień pod spodem
  const actionEndRef = useRef<HTMLDivElement | null>(null);
  const [isActionStuck, setIsActionStuck] = useState(false);

  const {
    canCurate, isInCatalog, markInCatalog, addToCatalog, addingToCatalogId, fillFromCatalog, isFillingFromCatalog,
  } = useWordsCatalog({
    isDataSynced, wordsList, formikRef, setWords,
  });

  // ✅ FIX 2: Przechowuj poprzednie ID żeby wykryć przełączenie
  const previousIdRef = useRef<string | undefined>(undefined);

  const validationSchema = getValidationSchema(t);

  // ✅ FIX 3: Bezpieczne initialValues z weryfikacją ID
  const initialValues = useMemo(() => {
    // Jeśli wordsList nie odpowiada aktualnemu ID, zwróć puste wartości
    if (!wordsList || String(wordsList.id) !== id) {
      return {
        name: '',
        description: '',
        words: [],
      };
    }

    return {
      name: wordsList.name || '',
      description: wordsList.description || '',
      words: withUnsavedWords(wordsList.words || [], submittedRef.current),
    };
  }, [wordsList, id]);

  // Ostatnia zapisana wersja każdej karty
  const savedWordsById = useMemo(() => getSavedWordsById(wordsList?.words || []), [wordsList]);

  // Ostatnia zapisana wersja całej listy, w kształcie formularza i bez niezapisanych kart z initialValues
  const savedValues = useMemo(() => ({
    name: wordsList?.name || '',
    description: wordsList?.description || '',
    words: wordsList?.words || [],
  }), [wordsList]);

  // ✅ FIX 4: Reset stanu przy zmianie ID
  useEffect(() => {
    if (id !== previousIdRef.current) {
      console.log(`🔄 Switching from list ${previousIdRef.current} to ${id}`);

      // Natychmiast zablokuj auto-save
      setIsDataSynced(false);

      // Reset lokalnego stanu słów
      setWords([]);

      // Niezapisane karty poprzedniej listy nie mogą wrócić na tej
      submittedRef.current = null;

      // Reset formularza jeśli istnieje
      if (formikRef.current) {
        formikRef.current.resetForm();
      }

      previousIdRef.current = id;
    }
  }, [id]);

  // ✅ FIX 5: Ładowanie danych z weryfikacją
  const loadData = useCallback(() => {
    if (id) {
      setIsDataSynced(false); // Zablokuj auto-save przed ładowaniem
      dispatch(fetchWordsListData(id));
    }
  }, [id, dispatch]);

  // ✅ FIX 6: Synchronizacja danych tylko gdy wordsList odpowiada ID
  useEffect(() => {
    if (wordsList && String(wordsList.id) === id) {
      console.log(`✅ Data synced for list ${id}`, {
        name: wordsList.name,
        wordsCount: wordsList.words?.length,
      });

      // Formularz tak, jak jest na ekranie: `formikRef` trzyma stan z ostatniego renderu, sprzed przeładowania
      // nowymi initialValues. Inny niż wysłany znaczy, że w trakcie zapisu ktoś pisał — tego serwer nie dostał
      const onScreen = formikRef.current?.values;
      const isEditedWhileSaving = Boolean(onScreen && submittedRef.current && !isEqual(onScreen, submittedRef.current.values));

      // Te same karty co w formularzu, łącznie z niezapisanymi
      setWords(isEditedWhileSaving ? onScreen.words : initialValues.words);
      // Po zapisie formularz bierze to, co przyszło z serwera, także gdy niczym się nie różni od poprzedniego
      // stanu — Formik przeładowuje się sam tylko przy zmianie, a wyczyszczony zwrot ma wrócić od razu
      formikRef.current?.resetForm({ values: initialValues });

      // Zmiany z czasu zapisu zostają na ekranie. Punktem odniesienia jest już wersja z serwera, więc formularz
      // liczy się jako zmieniony: zapisze go następny autozapis albo przycisk
      if (isEditedWhileSaving) {
        formikRef.current?.setValues(onScreen);
      }

      setIsDataSynced(true); // Teraz auto-save jest bezpieczny
    } else if (wordsList && String(wordsList.id) !== id) {
      console.log(`⚠️ WordsList ID mismatch: have ${wordsList.id}, need ${id}`);
      setIsDataSynced(false);
    }
  }, [wordsList, id, initialValues]);

  // Ładuj dane przy zmianie ID
  useEffect(() => {
    loadData();
  }, [id, loadData]);

  // ✅ FIX 7: Bezpieczny onSubmit z weryfikacją
  // const onSubmit = useCallback(async (values: {
  //   name: string;
  //   description: string;
  //   words: any[];
  // }) => {
  //   // Dodatkowe zabezpieczenie - sprawdź czy ID się zgadza
  //   if (!id || !wordsList || String(wordsList.id) !== id) {
  //     console.error('❌ Cannot save: ID mismatch', {
  //       urlId: id,
  //       wordsListId: wordsList?.id,
  //     });
  //     error('Błąd synchronizacji danych. Odśwież stronę.');

  //     return;
  //   }

  //   console.log(`💾 Saving list ${id}:`, {
  //     name: values.name,
  //     wordsCount: values.words?.length,
  //   });

  //   // ✅ NIE wysyłaj sharedStudentIds przy normalnym zapisie!
  //   dispatch(saveWordList(id, {
  //     name: values.name,
  //     description: values.description,
  //     words: values.words,
  //     // sharedStudentIds - celowo pominięte!
  //   }, true));

  //   await dispatch(fetchFolderTreeWithLists(true) as any);

  //   if (activeList && activeList.id === Number(id)) {
  //     if (activeList.words !== values.words) {
  //       const updatedActiveList = { ...activeList, words: values.words };
  //       dispatch(setActiveList(updatedActiveList));
  //     }
  //   }
  // }, [activeList, dispatch, id, wordsList]);

  // Bloker „Zapisz zmiany”: okno z numerami kart bez zwrotu. Kursor trafia do pola „Zwrot” pierwszej
  // z nich dopiero w `afterClose` — wcześniej antd oddaje fokus przyciskowi, który otworzył okno
  const showTermRequiredModal = useCallback((cardNumbers: number[]) => {
    Modal.warning({
      className: classes.termRequiredModal,
      title: t(
        cardNumbers.length === 1 ? 'words-list:wordsListForm.termRequiredModal.titleOne' : 'words-list:wordsListForm.termRequiredModal.titleMany',
        { cards: cardNumbers.join(', ') },
      ),
      content: t('words-list:wordsListForm.termRequiredModal.content'),
      okText: t('words-list:wordsListForm.termRequiredModal.ok'),
      afterClose: () => {
        const input = document.querySelector<HTMLInputElement>(`input[name="words[${cardNumbers[0] - 1}].term"]`);

        input?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        input?.focus({ preventScroll: true });
      },
    });
  }, [t]);

  // Zmodyfikowana funkcja onSubmit
  const onSubmit = useCallback(async (values: {
    name: string;
    description: string;
    words: any[];
  }) => {
  // ✅ Blokada podwójnych kliknięć
    if (isSubmitting) {
      console.log('⏳ Submit already in progress, skipping...');

      return;
    }

    // Dodatkowe zabezpieczenie - sprawdź czy ID się zgadza
    if (!id || !wordsList || String(wordsList.id) !== id) {
      console.error('❌ Cannot save: ID mismatch', {
        urlId: id,
        wordsListId: wordsList?.id,
      });
      error(t('words-list:editor.syncError'));

      return;
    }

    // „Zapisz zmiany” nie zapisuje nic, dopóki któraś karta nie ma zwrotu. Autozapis tego nie sprawdza —
    // zapisuje resztę listy, a karty bez zwrotu pomija (getWordToSave)
    if (!isAutoSaveRef.current) {
      const cardsWithoutTerm = values.words
        .map((word, index) => (getTermRequiredKey(word, savedWordsById) ? index + 1 : 0))
        .filter(Boolean);

      if (cardsWithoutTerm.length) {
        showTermRequiredModal(cardsWithoutTerm);

        return;
      }
    }

    setIsSubmitting(true); // ✅ Zablokuj kolejne kliknięcia

    try {
      // Karty bez zwrotu nie trafiają na serwer w nowej postaci, zostają tylko na ekranie
      const sent = values.words.map((word) => getWordToSave(word, savedWordsById));
      const wordsToSave = sent.filter((word) => word !== undefined);
      submittedRef.current = { values, sent };

      console.log(`💾 Saving list ${id}:`, {
        name: values.name,
        wordsCount: wordsToSave.length,
      });

      // ✅ NIE wysyłaj sharedStudentIds przy normalnym zapisie!
      // Czekamy na sam zapis: bez tego blokada zwalniała się przed jego końcem, a catch poniżej
      // raportował błąd odświeżenia drzewa jako nieudany zapis. Drzewo odświeża już sam thunk
      // (slice/actions.ts:66), więc drugie pobranie stąd tylko ścigało się z tamtym.
      await dispatch(saveWordList(id, {
        name: values.name,
        description: values.description,
        words: wordsToSave,
      // sharedStudentIds - celowo pominięte!
      }, true) as any);

      if (activeList && activeList.id === Number(id)) {
        if (activeList.words !== wordsToSave) {
          const updatedActiveList = { ...activeList, words: wordsToSave };
          dispatch(setActiveList(updatedActiveList));
        }
      }
    } catch (err) {
      console.error('❌ Save failed:', err);
      error(t('words-list:save.messages.error'));
    } finally {
      setIsSubmitting(false); // ✅ Odblokuj po zakończeniu (sukces lub błąd)
    }
  }, [activeList, dispatch, id, wordsList, isSubmitting, savedWordsById, showTermRequiredModal, t]);

  // ✅ FIX 8: Auto-save z pełnym zabezpieczeniem
  useEffect(() => {
  // Wyczyść poprzedni timer
    if (autoSaveTimerRef.current) {
      clearInterval(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }

    // Nie uruchamiaj auto-save jeśli dane nie są zsynchronizowane
    if (!isDataSynced || !id) {
      console.log('⏸️ Auto-save disabled - data not synced');

      return () => {};
    }

    console.log(`▶️ Auto-save enabled for list ${id}`);

    autoSaveTimerRef.current = setInterval(() => {
    // Potrójne zabezpieczenie przed zapisem złych danych
      const canSave = isDataSynced
      && formikRef.current
      && formikRef.current.dirty
      && formikRef.current.isValid
      && wordsList
      && String(wordsList.id) === id;

      if (canSave) {
        console.log(`⏰ Auto-saving list ${id}...`);
        isAutoSaveRef.current = true;
        formikRef.current!.submitForm().finally(() => {
          isAutoSaveRef.current = false;
        });
      } else if (formikRef.current?.dirty) {
        console.log('⚠️ Auto-save skipped - safety check failed', {
          isDataSynced,
          wordsListId: wordsList?.id,
          urlId: id,
        });
      }
    }, AUTO_SAVE_INTERVAL);

    return () => {
      if (autoSaveTimerRef.current) {
        clearInterval(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
    };
  }, [id, isDataSynced, wordsList]);

  const handleImageSelected = useCallback((image: SelectedImage, values: any, setFieldValue: any, fromCatalog?: boolean) => {
    const { url, imageAssetId, ...imageMeta } = image;
    const newWords = values.words.map((word: any) => {
      if (word.id === selectedWordId) {
        return {
          ...word,
          image: url,
          imageMeta,
          // Jawne `null` przy uploadzie: podmiana zdjęcia z banku na własne musi wyczyścić
          // stare `imageAssetId`, a pominięcie pola zostawiłoby w słowie poprzednie
          imageAssetId: imageAssetId ?? null,
        };
      }

      return word;
    });

    setFieldValue('words', newWords);
    setWords(newWords);

    // Szuflada pytała katalog właśnie o ten zwrot, więc ta para na pewno w nim jest
    if (fromCatalog && imageAssetId) {
      markInCatalog([{ term: selectedWordTerm, assetId: imageAssetId }]);
    }

    setSelectedWordId(null);
    setSelectedWordTerm('');
  }, [selectedWordId, selectedWordTerm, markInCatalog]);

  const addWord = useCallback((values, setFieldValue) => {
    const newWords = [
      ...(values.words || []),
      {
        id: nanoid(),
        term: '',
        definition: '',
        image: '',
      },
    ];
    setFieldValue('words', newWords);
    setWords(newWords);
  }, []);

  const removeWord = useCallback((wordId, values, setFieldValue) => {
    const newWords = (values.words || []).filter((word: any) => word.id !== wordId);
    setFieldValue('words', newWords);
    setWords(newWords);
  }, []);

  const openImageDrawer = useCallback((wordId: string, values: any) => {
    const selectedWord = values.words.find((word: any) => word.id === wordId);
    if (selectedWord) {
      setSelectedWordId(wordId);
      setSelectedWordTerm(selectedWord.term);
    }
  }, []);

  const closeImageDrawer = useCallback(() => {
    setSelectedWordId(null);
    setSelectedWordTerm('');
  }, []);

  const onSuccess = useCallback(() => {
    success(t('words-list:save.messages.success'));
  }, [t]);

  const onError = useCallback(() => {
    error(t('words-list:save.messages.error'));
  }, [t]);

  useActionStatus(onSuccess, onError, {
    isLoadingSelector: getIsSaving,
    errorSelector: getIsSavingError,
  });

  // Znacznik końca formularza istnieje dopiero po wczytaniu listy (wcześniej ekran pokazuje tylko kręciołek)
  useEffect(() => {
    const actionEnd = actionEndRef.current;

    if (!actionEnd) {
      return undefined;
    }

    const observer = new IntersectionObserver(([entry]) => setIsActionStuck(!entry.isIntersecting));
    observer.observe(actionEnd);

    return () => observer.disconnect();
  }, [loading, isDataSynced]);

  // ✅ FIX 9: Pokazuj loading gdy dane nie są zsynchronizowane
  if (loading || !isDataSynced) {
    return (
      <div className={classes.wrapper}>
        <Spin tip={loading ? t('common:loading') : t('words-list:editor.syncing')} />
      </div>
    );
  }

  return (
    <>
      <div className={classes.wrapper}>
        <Formik
          innerRef={(formik) => { formikRef.current = formik; }}
          initialValues={initialValues}
          onSubmit={(values) => onSubmit(values as any)}
          validationSchema={validationSchema}
          enableReinitialize
        >
          {({
            errors, touched, values, setFieldValue,
          }) => (
            <Form className={classes.form} layout="vertical" id="words-list">
              <div>
                <div className={classes.listMeta}>
                  <div>
                    <span className={classes.listMetaLabel}>{t('words-list:wordsListForm.labels.name')}</span>
                    <FormItem
                      name="name"
                      error={errors.name}
                      isTouched={touched.name}
                      value={values.name}
                      placeholderKey="words-list:editor.namePlaceholder"
                      className={classes.listField}
                    />
                  </div>

                  <div>
                    <span className={classes.listMetaLabel}>{t('words-list:wordsListForm.labels.description')}</span>
                    <FormItem
                      name="description"
                      error={errors.description}
                      isTouched={touched.description}
                      value={values.description}
                      placeholderKey="words-list:editor.descriptionPlaceholder"
                      className={classes.listField}
                    />
                  </div>
                </div>

                <div className={classes.itemsHeader}>
                  <span className={classes.itemsTitle}>{t('words-list:wordsListForm.labels.items')}</span>
                  <span className={`${classes.itemsCount} ${!words?.length ? classes.noItems : ''}`}>
                    {words?.length ? t(`words-list:itemsCount.${pluralForm(words.length)}`, { n: words.length }) : 0}
                  </span>
                </div>

                {(!words || !words.length) && (
                  <div className={classes.emptyList}>
                    <TbListDetails className={classes.emptyListIcon} />
                    <p>{t('words-list:wordsListForm.info.wordsEmpty')}</p>
                    <p className={classes.emptyListHint}>{t('words-list:wordsListForm.info.wordsEmptyHint')}</p>
                  </div>
                )}

                {words && (
                  <>
                    {words.map((word, index) => (
                      <Card
                        key={word.id}
                        className={classes.wordCard}
                        style={styles.wordContainer}
                        bodyStyle={styles.wordContainerBody}
                        headStyle={styles.wordContainerHead}
                        title={<span className={classes.wordNumber}>{index + 1}</span>}
                        bordered={false}
                        extra={(
                          <div className={classes.cardActions}>
                            {canCurate && (
                              <div className={classes.catalogSlot}>
                                <CatalogWordButton
                                  word={values.words?.[index]}
                                  isInCatalog={isInCatalog(values.words?.[index])}
                                  isAdding={addingToCatalogId === word.id}
                                  onAdd={addToCatalog}
                                />
                              </div>
                            )}
                            <div className={classes.wordActions}>
                              <button
                                type="button"
                                className={classes.removeWordButton}
                                onClick={() => removeWord(word.id, values, setFieldValue)}
                              >
                                <Thrash className={classes.removeWordIcon} aria-hidden="true" />
                              </button>
                            </div>
                          </div>
                        )}
                      >
                        <Card.Grid
                          className={classes.inputs}
                          hoverable={false}
                        >
                          <FormItem
                            name={`words[${index}].term`}
                            labelKey="words-list:wordsListForm.labels.term"
                            placeholderKey="words-list:wordsListForm.labels.term"
                            value={word.term}
                            requiredTooltipKey={getTermRequiredKey(values.words?.[index], savedWordsById)}
                            componentAdditionalProps={WORD_FIELD_PROPS}
                          />
                        </Card.Grid>
                        <Card.Grid
                          className={classes.inputs}
                          hoverable={false}
                        >
                          <FormItem
                            name={`words[${index}].definition`}
                            labelKey="words-list:wordsListForm.labels.definition"
                            placeholderKey="words-list:wordsListForm.labels.definition"
                            value={word.definition}
                            componentAdditionalProps={WORD_FIELD_PROPS}
                          />
                        </Card.Grid>
                        <Card.Grid
                          className={classes.pictureWrapper}
                          hoverable={false}
                        >
                          <div
                            role="button"
                            tabIndex={0}
                            onClick={() => openImageDrawer(word.id, values)}
                            onKeyPress={() => openImageDrawer(word.id, values)}
                            className={`${classes.imageButtonContainer} ${(word.image && word.imageMeta?.authorName) || isUnsplashWithoutAuthor(word) ? classes.imageButtonWithCredit : ''} ${!word.image ? classes.imageButtonEmpty : ''}`}
                          >
                            {word.image ? (
                              <img
                                className={classes.img}
                                src={word.image}
                                alt={word.term}
                                onError={(e) => {
                                  console.error('❌ Image failed to load:', word.image);
                                  e.currentTarget.style.border = '2px solid red';
                                }}
                              />
                            ) : (
                              <>
                                <TbPhotoPlus className={classes.imageButtonIcon} />
                                <span className={classes.imageButtonTitle}>
                                  {t('words-list:wordsListForm.labels.image')}
                                </span>
                              </>
                            )}
                          </div>
                          {word.image && word.imageMeta?.authorName && (
                            <div className={classes.imageCredit}>
                              {t('words-list:credits.photoBy')}{' '}
                              <a
                                href={withUnsplashUtm(word.imageMeta.authorUrl)}
                                target="_blank"
                                rel="noreferrer"
                              >
                                {word.imageMeta.authorName}
                              </a>
                              {' · '}
                              <a
                                href={withUnsplashUtm(word.imageMeta.sourceUrl)}
                                target="_blank"
                                rel="noreferrer"
                              >
                                Unsplash
                              </a>
                            </div>
                          )}
                          {/* Zdjęcie sprzed września: autora nie da się odzyskać, źródło trzeba wskazać i tak */}
                          {isUnsplashWithoutAuthor(word) && (
                            <div className={classes.imageCredit}>
                              {t('words-list:credits.photoBy')}{' '}
                              <a
                                href={withUnsplashUtm('https://unsplash.com/')}
                                target="_blank"
                                rel="noreferrer"
                              >
                                Unsplash
                              </a>
                            </div>
                          )}
                        </Card.Grid>
                      </Card>
                    ))}
                  </>
                )}

                {getWordsToFill(values.words).length > 0 && (
                  <div className={classes.catalogBar}>
                    <Tooltip title={t('words-list:catalog.fill.hint')}>
                      <Button
                        className={classes.fillButton}
                        onClick={fillFromCatalog}
                        loading={isFillingFromCatalog}
                      >
                        {t('words-list:catalog.fill.button', { missing: getWordsToFill(values.words).length })}
                      </Button>
                    </Tooltip>
                  </div>
                )}

                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => addWord(values, setFieldValue)}
                  onKeyPress={() => addWord(values, setFieldValue)}
                  className={classes.addWordButtonContainer}
                >
                  {t('words-list:button.addWord')}
                  <TbSquareRoundedPlus className={classes.addWordIcon} />
                </div>
              </div>

              <div className={`${classes.action} ${isActionStuck ? classes.actionStuck : ''}`}>
                <SaveButton savedValues={savedValues} isSubmitting={isSubmitting} />
              </div>
              <div ref={actionEndRef} className={classes.actionEnd} />

              <ImageSelectionDrawer
                visible={!!selectedWordId}
                wordsListId={id}
                selectedWordId={selectedWordId}
                onClose={closeImageDrawer}
                onImageSelected={(image, fromCatalog) => handleImageSelected(image, values, setFieldValue, fromCatalog)}
                initialSearchTerm={selectedWordTerm}
              />
            </Form>
          )}
        </Formik>
      </div>
    </>
  );
};

export default withTagManager(WordsListView);

// // src/views/words-list/WordsListView.tsx
// /* eslint-disable max-len */
// /* eslint-disable no-param-reassign */
// import React, {
//   FC, useCallback, useEffect, useState, useRef,
// } from 'react';
// import { nanoid } from 'nanoid';
// import { Formik, FormikProps } from 'formik';
// import { Form, SubmitButton } from 'formik-antd/lib';
// import { useTranslation } from 'react-i18next';
// import { useParams } from 'react-router';
// import { useDispatch, useSelector } from 'react-redux';
// import {
//   Button, Card, Spin,
// } from 'antd';
// import { FileImageOutlined } from '@ant-design/icons';
// import { ReactComponent as Thrash } from '../../assets/icons/teacher/trash.svg';
// import classes from './WordsListView.module.less';
// import styles from './WordsListView.styles';
// import withTagManager from '../../hooks/withTagManager';
// import FormItem from '../../components/FormItem/FormItem';
// import { getValidationSchema } from './WordsList.schema';
// import {
//   getIsLoadingWordsListData, getIsSaving, getIsSavingError, getWordsListData,
// } from './slice/selectors';
// import { fetchWordsListData, saveWordList } from './slice/actions';
// import { error, success } from '../../components/Message';
// import useActionStatus from '../../hooks/useActionStatus';
// import { getActiveList } from '../auth/slice/selectors';
// import { setActiveList } from '../auth/slice/actions';
// import { ImageSelectionDrawer } from '../../components/ImageSelectionDrawer/ImageSelectionDrawer';
// import { fetchFolderTreeWithLists } from '../folders/slice/actions';

// // Auto-save co 2 minuty
// const AUTO_SAVE_INTERVAL = 60000;

// const WordsListView: FC = () => {
//   const { id } = useParams<{ id?: string | undefined }>();
//   const dispatch = useDispatch();
//   const { t } = useTranslation();
//   const [words, setWords] = useState<any[]>([]);
//   const [selectedWordId, setSelectedWordId] = useState<string | null>(null);
//   const [selectedWordTerm, setSelectedWordTerm] = useState<string>('');
//   const activeList = useSelector(getActiveList);

//   const loading = useSelector(getIsLoadingWordsListData);
//   const wordsList = useSelector(getWordsListData);

//   const formikRef = useRef<FormikProps<any> | null>(null);
//   const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);

//   const validationSchema = getValidationSchema(t);

//   const initialValues: {
//     name: string;
//     description: string;
//     sharedStudentIds: number[];
//     words: any[];
//   } = wordsList
//     ? {
//       name: wordsList.name,
//       description: wordsList.description,
//       sharedStudentIds: wordsList.sharedStudentIds || [],
//       words: wordsList.words || [],
//     } : {
//       name: '',
//       description: '',
//       sharedStudentIds: [],
//       words: [],
//     };

//   const loadData = useCallback(() => {
//     if (id) {
//       dispatch(fetchWordsListData(id));
//     }
//   }, [id, dispatch]);

//   const onSubmit = useCallback(async (values: {
//     name: string;
//     description: string;
//     sharedStudentIds: number[];
//     words: any[];
//   }) => {
//     if (id) {
//       // skipRedirect = true - NIE zamykaj widoku
//       dispatch(saveWordList(id, values, true));

//       await dispatch(fetchFolderTreeWithLists(true) as any);

//       if (activeList) {
//         if (activeList.words !== values.words) {
//           const updatedActiveList = { ...activeList, words: values.words };
//           dispatch(setActiveList(updatedActiveList));
//         }
//       }
//     }
//   }, [activeList, dispatch, id]);

//   const handleImageSelected = useCallback((url: string, values: any, setFieldValue: any) => {
//     const newWords = values.words.map((word: any) => {
//       if (word.id === selectedWordId) {
//         return {
//           ...word,
//           image: url,
//         };
//       }

//       return word;
//     });

//     setFieldValue('words', newWords); // ⬅️ To oznacza formularz jako dirty
//     setWords(newWords);
//     setSelectedWordId(null);
//     setSelectedWordTerm('');
//   }, [selectedWordId]);

//   const addWord = useCallback((values, setFieldValue) => {
//     const newWords = [
//       ...(values.words || []),
//       {
//         id: nanoid(),
//         term: '',
//         definition: '',
//         image: '',
//       },
//     ];
//     setFieldValue('words', newWords);
//     setWords(newWords);
//     // values.words = newWords;
//   }, []);

//   const removeWord = useCallback((wordId, values, setFieldValue) => {
//     const newWords = (values.words || []).filter((word: any) => word.id !== wordId);
//     setFieldValue('words', newWords);
//     setWords(newWords);
//     // values.words = newWords;
//   }, []);

//   const openImageDrawer = useCallback((wordId: string, values: any) => {
//     const selectedWord = values.words.find((word: any) => word.id === wordId);
//     if (selectedWord) {
//       setSelectedWordId(wordId);
//       setSelectedWordTerm(selectedWord.term);
//     }
//   }, []);

//   const closeImageDrawer = useCallback(() => {
//     setSelectedWordId(null);
//     setSelectedWordTerm('');
//   }, []);

//   useEffect(() => {
//   // Reset przy zmianie listy
//     setWords([]);
//   }, [id]);

//   useEffect(() => {
//     if (wordsList) {
//       setWords(wordsList.words || []);
//     }
//   }, [wordsList]);

//   useEffect(() => {
//     loadData();
//   }, [id, loadData]);

//   useEffect(() => {
//     if (autoSaveTimerRef.current) {
//       clearInterval(autoSaveTimerRef.current);
//     }

//     autoSaveTimerRef.current = setInterval(() => {
//       if (formikRef.current && formikRef.current.dirty && formikRef.current.isValid) {
//         formikRef.current.submitForm();
//       }
//     }, AUTO_SAVE_INTERVAL);

//     return () => {
//       if (autoSaveTimerRef.current) {
//         clearInterval(autoSaveTimerRef.current);
//       }
//     };
//   }, [id]);

//   const onSuccess = useCallback(() => {
//     success(t('words-list:save.messages.success'));
//   }, [t]);

//   const onError = useCallback(() => {
//     error(t('words-list:save.messages.error'));
//   }, [t]);

//   useActionStatus(onSuccess, onError, {
//     isLoadingSelector: getIsSaving,
//     errorSelector: getIsSavingError,
//   });

//   if (loading) {
//     return <Spin />;
//   }

//   return (
//     <>
//       <div className={classes.wrapper}>
//         <Formik
//           innerRef={(formik) => { formikRef.current = formik; }}
//           initialValues={initialValues}
//           onSubmit={(values) => onSubmit(values as any)}
//           validationSchema={validationSchema}
//           enableReinitialize
//         >
//           {({
//             errors, touched, values, setFieldValue,
//           }) => (
//             <Form className={classes.form} layout="vertical" id="words-list">
//               <div>
//                 <FormItem
//                   name="name"
//                   error={errors.name}
//                   isTouched={touched.name}
//                   value={values.name}
//                   placeholderKey="nazwa listy"
//                 />

//                 <FormItem
//                   name="description"
//                   error={errors.description}
//                   isTouched={touched.description}
//                   value={values.description}
//                   placeholderKey="opis listy"
//                 />

//                 <div className={classes.separatorLine} />

//                 {
//                     (!words || !words.length) && (
//                       <Card style={styles.wordContainer} bordered={false}>
//                         <Card.Grid hoverable={false} className={classes.emptyList}>
//                           {t('words-list:wordsListForm.info.wordsEmpty')}
//                         </Card.Grid>
//                       </Card>
//                     )
//                   }

//                 {
//                     words && (
//                       <>
//                         {
//                           words.map((word, index) => (
//                             <Card
//                               key={word.id}
//                               style={styles.wordContainer}
//                               bodyStyle={styles.wordContainerBody}
//                               headStyle={styles.wordContainerHead}
//                               title={index + 1}
//                               bordered={false}
//                               extra={(
//                                 <Button
//                                   onClick={() => removeWord(word.id, values, setFieldValue)}
//                                   style={{ background: 'transparent', border: 'none' }}
//                                   icon={<Thrash style={{ fill: 'red' }} />}
//                                 />
//                               )}
//                             >
//                               <Card.Grid
//                                 className={classes.inputs}
//                                 hoverable={false}
//                               >
//                                 <FormItem
//                                   name={`words[${index}].term`}
//                                   labelKey="words-list:wordsListForm.labels.term"
//                                   placeholderKey="words-list:wordsListForm.labels.term"
//                                   value={word.term}
//                                 />
//                               </Card.Grid>
//                               <Card.Grid
//                                 className={classes.inputs}
//                                 hoverable={false}
//                               >
//                                 <FormItem
//                                   name={`words[${index}].definition`}
//                                   labelKey="words-list:wordsListForm.labels.definition"
//                                   placeholderKey="words-list:wordsListForm.labels.definition"
//                                   value={word.definition}
//                                 />
//                               </Card.Grid>
//                               <Card.Grid
//                                 className={classes.pictureWrapper}
//                                 hoverable={false}
//                               >
//                                 <div
//                                   role="button"
//                                   tabIndex={0}
//                                   onClick={() => openImageDrawer(word.id, values)}
//                                   onKeyPress={() => openImageDrawer(word.id, values)}
//                                   className={classes.imageButtonContainer}
//                                 >
//                                   {word.image ? (
//                                     <img
//                                       className={classes.img}
//                                       src={word.image}
//                                       alt={word.term}
//                                       onError={(e) => {
//                                         console.error('❌ Image failed to load:', word.image);
//                                         console.error('Word term:', word.term);
//                                         e.currentTarget.style.border = '2px solid red';
//                                       }}
//                                     />
//                                   ) : (
//                                     <>
//                                       <FileImageOutlined style={{ fontSize: '2em' }} />
//                                       <span className={classes.imageButtonTitle}>
//                                         {t('words-list:wordsListForm.labels.image')}
//                                       </span>
//                                     </>
//                                   )}
//                                 </div>
//                               </Card.Grid>
//                             </Card>
//                           ))
//                         }
//                       </>
//                     )
//                   }

//                 <div
//                   role="button"
//                   tabIndex={0}
//                   onClick={() => addWord(values, setFieldValue)}
//                   onKeyPress={() => addWord(values, setFieldValue)}
//                   className={classes.addWordButtonContainer}
//                 >
//                   {t('words-list:button.addWord')}
//                 </div>
//               </div>

//               <div className={classes.action}>
//                 <SubmitButton
//                   type="primary"
//                   loading={loading}
//                   id={classes.submitt}
//                 >
//                   {t('words-list:button.save')}
//                 </SubmitButton>
//               </div>

//               <ImageSelectionDrawer
//                 visible={!!selectedWordId}
//                 wordsListId={id || ''}
//                 selectedWordId={selectedWordId}
//                 onClose={closeImageDrawer}
//                 onImageSelected={(url) => handleImageSelected(url, values, setFieldValue)}
//                 initialSearchTerm={selectedWordTerm}
//               />

//             </Form>
//           )}
//         </Formik>
//       </div>
//     </>
//   );
// };

// export default withTagManager(WordsListView);
